<script lang="ts">
	import { onMount } from 'svelte';
	import type { GetLiveDropInfoQuery } from '../../queries/__generated__/liveDropInfoQueryCache.generated';
	import type { LiveDropInfo, LiveDropInfoResponse } from '../../types';
	import { getHybridDropConfig } from '../../utils/getHybridDropConfig/getHybridDropConfig';
	import { getLiveSlug } from '../../utils/getLiveSlug/getLiveSlug';
	import { getSetDiscountConfigs } from '../../utils/getSetDiscountConfigs/getSetDiscountConfigs';
	import { getLiveDropInfoContext } from '../utils/getLiveDropInfoContext/getLiveDropInfoContext';
	import { Config } from '#lib/constants/config.js';
	import { DropType } from '#lib/features/checkout/constants/drop-types.js';
	import { getDirectusDateTimestamp } from '$global/utils/getDirectusDateTimestamp';

	let { liveDropInfoCtx } = getLiveDropInfoContext();
	let { setLiveDropInfo, setLoading } = liveDropInfoCtx;

	onMount(() => {
		getLiveInfo();
	});

	const getLiveStatus = ({
		endDate,
		currentTime,
	}: {
		endDate: string;
		currentTime: number;
	}): boolean => {
		return currentTime < getDirectusDateTimestamp(endDate);
	};

	const getEditions = (data: GetLiveDropInfoQuery) => {
		const currentTime = new Date().getTime();

		const editions: LiveDropInfoResponse['editions'] = {};

		data.Drop_Config.filter(
			(dropConfig) => dropConfig.platform === 'editions'
		).forEach((dropConfig) => {
			const hybridConfig = getHybridDropConfig(dropConfig.additional_options);
			const setDiscounts = getSetDiscountConfigs(dropConfig.additional_options);

			(dropConfig.products ?? []).forEach((product) => {
				(product?.editions ?? []).forEach((edition) => {
					const checkoutEndDate = dropConfig.checkout_end_date;

					editions[edition?.code || ''] = {
						productId: product?.id || '',
						// A hybrid drop's products each set their own flow; a product
						// without one follows its drop config.
						type: product?.drop_config_type || dropConfig?.drop_type || '',
						hybridConfig,
						setDiscounts,
						isGeneric: false,
						isProcessedOffline: false,
						isLive: getLiveStatus({ endDate: checkoutEndDate, currentTime }),
						isCartOpen: getLiveStatus({
							endDate: dropConfig.cart_end_date,
							currentTime,
						}),
						checkoutEndDate: checkoutEndDate,
						code: edition?.code || '',
						slug: dropConfig?.drop_page?.slug || '',
						platform: dropConfig.platform,
					};
				});
			});
		});

		return editions;
	};

	const getSeries = (data: GetLiveDropInfoQuery) => {
		const currentTime = new Date().getTime();

		const series: Record<string, LiveDropInfo> = {};

		data.Drop_Config.forEach((dropConfig) => {
			(dropConfig.products ?? []).forEach((product) => {
				(product?.editions ?? []).forEach((edition) => {
					if (edition?.series?.code && !series[edition?.series?.code]) {
						const checkoutEndDate = dropConfig.checkout_end_date;

						series[edition?.series?.code || ''] = {
							type: dropConfig?.drop_type || '',
							isGeneric: false,
							isProcessedOffline: false,
							isLive: getLiveStatus({ endDate: checkoutEndDate, currentTime }),
							checkoutEndDate: checkoutEndDate,
							code: edition?.series?.code || '',
							slug: dropConfig?.drop_page?.slug || '',
							platform: dropConfig.platform,
						};
					}
				});
			});
		});

		return series;
	};

	const getPrimary = (data: GetLiveDropInfoQuery) => {
		const currentTime = new Date().getTime();

		const primary: Record<string, LiveDropInfo> = {};

		data.Primary_Collection.forEach((item) => {
			const dropConfig = item?.drop_page?.drop_config?.[0];
			const checkoutEndDate = dropConfig?.checkout_end_date ?? '';

			primary[item?.code || ''] = {
				type: dropConfig?.is_processed_offline
					? DropType.Application
					: DropType.Purchase,
				isProcessedOffline: !!dropConfig?.is_processed_offline,
				isGeneric: !!dropConfig?.products?.length,
				isLive: getLiveStatus({ endDate: checkoutEndDate, currentTime }),
				checkoutEndDate,
				code: item?.code || '',
				slug: item?.drop_page?.slug || '',
				platform: 'primary',
			};
		});

		return primary;
	};

	const getLiveSlugs = ({
		editions,
		series,
		primary,
	}: {
		editions: Record<string, LiveDropInfo>;
		series: Record<string, LiveDropInfo>;
		primary: Record<string, LiveDropInfo>;
	}) => {
		const liveDrops = [
			...Object.values(editions),
			...Object.values(series),
			...Object.values(primary),
		].filter((drop) => drop.isLive && drop.slug);

		const slugs = new Set(
			liveDrops.map((drop) => getLiveSlug(drop.slug, drop.platform))
		);

		return Array.from(slugs).filter(Boolean);
	};

	const getLiveInfo = async () => {
		try {
			const res = await fetch(
				`${Config.GraphqlApiDomain}/queryCache?queryType=getLiveDropInfo`
			);

			const info: GetLiveDropInfoQuery = (await res.json()).data;

			const editions = getEditions(info);
			const series = getSeries(info);
			const primary = getPrimary(info);
			const liveSlugs = getLiveSlugs({
				editions,
				series,
				primary,
			});
			const liveEditionsCodes = Object.values(editions)
				.filter((drop) => drop.isLive)
				.map((drop) => drop.code);
			const cartOpenEditionsCodes = Object.values(editions)
				.filter((drop) => drop.isCartOpen)
				.map((drop) => drop.code);
			const liveSeriesCodes = Object.values(series)
				.filter((drop) => drop.isLive)
				.map((drop) => drop.code);

			const liveDropInfo: LiveDropInfoResponse = {
				editions,
				series,
				primary,
				liveSlugs,
				liveEditionsCodes,
				cartOpenEditionsCodes,
				liveSeriesCodes,
				raw: info,
			};

			setLiveDropInfo(liveDropInfo);
			// eslint-disable-next-line no-empty
		} catch {
		} finally {
			setLoading(false);
		}
	};
</script>
